/**
 * Consumer Portal PageTemplate
 *
 * Thin wrapper around the design system's PageTemplate that injects
 * portal-specific context (auth, consumer type, business branding, navigation).
 *
 * Pages import this instead of Layout + usePageMetadata:
 *
 * ```tsx
 * import { PageTemplate } from '../../components/layout/PageTemplate';
 *
 * export default function DocumentsPage() {
 *   return (
 *     <PageTemplate variant="list" titleKey="navigation.documents" icon={DocIcon}>
 *       {content}
 *     </PageTemplate>
 *   );
 * }
 * ```
 *
 * @see PR #856 — Navigation & Layout Redesign (PageTemplate pattern)
 */

import {
  PageTemplate as DSPageTemplate,
  type PageVariant,
  type SvgIconProps,
  useDesignSystem,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { getConsumerNavConfig, type NavTab, type VisibilityContext } from '@ops-matrix/shared-types';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { env } from '../../config/env';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useConsumerType } from '../../contexts/ConsumerTypeContext';
import { useBusinessBranding, useListPendingSignatures } from '../../lib/trpc/hooks';
import { BANNER_HEIGHT, ImpersonationBanner } from '../ImpersonationBanner/ImpersonationBanner';
import Breadcrumbs from './Breadcrumbs';
import { Header } from './Header';

// ============================================================================
// Types — Only expose page-level props (not layout config props)
// ============================================================================

export interface PageTemplateProps {
  /** i18n translation key for page title */
  titleKey?: string;
  /** Raw string title (for dynamic content) */
  title?: string;
  /** i18n translation key for page description */
  descriptionKey?: string;
  /** Raw string description */
  description?: string;
  /** Page icon */
  icon?: React.ComponentType<SvgIconProps>;
  /** Badges next to title */
  badges?: React.ReactNode;
  /** Action buttons in header */
  actions?: React.ReactNode;
  /** Back button callback */
  onBack?: () => void;
  /** Page variant (default: 'list') */
  variant?: PageVariant;
  /** Override default maxWidth (number in px, or MUI breakpoint, or false to disable) */
  maxWidth?: number | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** Opt out of variant padding */
  noPadding?: boolean;

  // --- Inline Actions ---
  /** Favorite toggle handler */
  onFavorite?: () => void;
  /** Whether the page is currently favorited */
  isFavorited?: boolean;
  /** Status chip rendered inline after the title */
  statusChip?: React.ReactNode;
  /** Dropdown menu items for the chevron button next to title */
  titleMenuItems?: React.ReactNode;

  // --- Tab Bar ---
  /** Page-level tabs (override nav-config section tabs when provided) */
  tabs?: NavTab[];
  /** Currently active tab ID */
  activeTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string, path: string) => void;

  /** Page content */
  children: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function PageTemplate({ children, ...pageProps }: PageTemplateProps) {
  const { consumer, isImpersonating, logout } = useConsumerAuth();
  const { consumerType } = useConsumerType();
  const { mode, toggleMode } = useDesignSystem();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch business branding for white-label experience (Issue #522)
  const { data: businessBranding } = useBusinessBranding(consumer?.businessId ?? null);

  // Update document title with business name for white-label experience
  useEffect(() => {
    if (businessBranding?.name) {
      document.title = businessBranding.name;
    } else {
      document.title = t('consumer.layout.defaultTitle');
    }
  }, [businessBranding?.name]);

  // Nav config with business name override
  const navConfig = useMemo(() => {
    const baseConfig = getConsumerNavConfig(consumerType);
    return {
      ...baseConfig,
      portalName: businessBranding?.name || baseConfig.portalName,
    };
  }, [consumerType, businessBranding?.name]);

  // Consumer portal has no CASL permissions, but it DOES have feature flags.
  // Wire `enable_termsheet_signing_v2` into the visibility context so
  // `requireFeatureFlag` in the nav config works correctly (#1308).
  const visibilityContext: VisibilityContext = useMemo(
    () => ({
      featureFlags: {
        enable_termsheet_signing_v2: env.VITE_ENABLE_TERMSHEET_SIGNING_V2,
      },
    }),
    [],
  );

  // Look up the `/documents-to-sign` nav item so its declarative
  // `badgePollInterval` drives the query's refetch cadence. Keeps the polling
  // decision at the config declaration site rather than hardcoded in the hook.
  const documentsToSignPollInterval = useMemo(() => {
    for (const section of navConfig.sections) {
      for (const item of section.items) {
        if (item.id === 'documents-to-sign') return item.badgePollInterval;
      }
    }
    return undefined;
  }, [navConfig.sections]);

  // Issue #1308 — Pending signatures nav badge count. Query is hook-gated by
  // the feature flag; returns empty when the flag is off so the badge stays 0
  // and the nav item is hidden by visibilityContext regardless. Cadence is
  // driven by the nav config's `badgePollInterval`; when unset the hook falls
  // back to its 60s default (safety net).
  const pendingSignaturesQuery = useListPendingSignatures({
    enabled: env.VITE_ENABLE_TERMSHEET_SIGNING_V2,
    refetchInterval: documentsToSignPollInterval,
  });
  const badgeCounts = useMemo<Record<string, number>>(
    () => ({
      'documents-to-sign': pendingSignaturesQuery.data?.pending?.length ?? 0,
    }),
    [pendingSignaturesQuery.data?.pending?.length],
  );

  return (
    <DSPageTemplate
      {...pageProps}
      pathname={location.pathname}
      onNavigate={navigate}
      navConfig={navConfig}
      visibilityContext={visibilityContext}
      badgeCounts={badgeCounts}
      impersonating={isImpersonating}
      bannerHeight={BANNER_HEIGHT}
      renderHeader={(props) => <Header {...props} />}
      renderBreadcrumbs={() => <Breadcrumbs />}
      renderBanner={() => (isImpersonating ? <ImpersonationBanner /> : null)}
      onLogoClick={() => navigate('/dashboard')}
      user={consumer ? { name: consumer.name ?? consumer.email, email: consumer.email } : undefined}
      onLogout={logout}
      onProfile={() => navigate('/profile')}
      isDarkMode={mode === 'dark' || mode === 'midnight'}
      onThemeToggle={toggleMode}
    >
      {children}
    </DSPageTemplate>
  );
}
